import { supabase } from './supabase';

/**
 * User Context Type
 * Single source of truth for authentication state and permissions
 */
export interface UserContext {
  isAuthenticated: boolean;
  email: string | null;
  role: 'super_admin' | 'ops_admin' | 'admin' | 'member' | 'client' | null;
  permissions: string[];
  profileMissing?: boolean; // Flag for missing profile
}

/**
 * Get User Context - Single Source of Truth
 * Use this function in client components
 * 
 * @returns UserContext object with authentication state and role
 */
export async function getUserContext(): Promise<UserContext> {
  const defaultContext: UserContext = {
    isAuthenticated: false,
    email: null,
    role: null,
    permissions: [],
  };

  try {
    // Get authenticated user (secure method)
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      return defaultContext;
    }

    // Get user profile with rbac_role - SINGLE SOURCE OF TRUTH
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('rbac_role, email')
      .eq('id', user.id)
      .single();

    // If profile doesn't exist or rbac_role is null, flag as missing profile
    if (!profile || !profile.rbac_role || profileError) {
      console.error('getUserContext: No valid role found for user', user.id);
      return {
        isAuthenticated: true,
        email: user.email || null,
        role: null,
        permissions: [],
        profileMissing: true, // Flag for missing profile
      };
    }

    let userRole = profile.rbac_role;
    const userEmail = profile.email || user.email || null;

    // HARD STOP: Force super_admin for hello@johnsmulo.com if database role is incorrect
    if (userEmail === 'hello@johnsmulo.com' && userRole !== 'super_admin') {
      console.warn(
        '⚠️ SECURITY WARNING: hello@johnsmulo.com had incorrect role in database:',
        userRole,
        '- Forcing super_admin in memory. Database trigger should prevent this.'
      );
      userRole = 'super_admin';
    }

    // Get permissions based on role
    const permissions = await getRolePermissions(user.id, userRole);

    return {
      isAuthenticated: true,
      email: userEmail,
      role: userRole as UserContext['role'],
      permissions,
    };
  } catch (error) {
    console.error('getUserContext error:', error);
    return defaultContext;
  }
}

/**
 * Get permissions for a user based on their role
 * Uses default role permissions (user_permissions table is optional)
 */
async function getRolePermissions(
  userId: string,
  role: string
): Promise<string[]> {
  // Just return default permissions based on role
  // The user_permissions table is optional and may not exist
  return getDefaultRolePermissions(role);
}

/**
 * Default permissions by role
 */
function getDefaultRolePermissions(role: string): string[] {
  const permissionMap: Record<string, string[]> = {
    super_admin: [
      'admin.manage_users',
      'admin.manage_roles',
      'admin.manage_organizations',
      'admin.view_audit',
      'admin.manage_plans',
      'portal.view',
      'portal.create_ticket',
      'portal.reply_ticket',
      'portal.upload_attachments',
      'portal.view_results',
      'team.view',
      'team.manage_tickets',
      'team.assign_tickets',
      'team.manage_skills',
      'analytics.view',
      'analytics.team',
      'analytics.client',
      'org.access_all',
      'user.impersonate',
      'user.export_data',
    ],
    ops_admin: [
      'admin.manage_users',
      'admin.manage_roles',
      'admin.manage_organizations',
      'admin.manage_plans',
      'admin.view_audit',
      'org.access_all',
      'analytics.view',
      'analytics.team',
      'analytics.client',
      'team.view',
      'team.manage_tickets',
      'team.assign_tickets',
      'team.manage_skills',
    ],
    admin: [
      'team.view',
      'team.manage_tickets',
      'team.assign_tickets',
      'team.manage_skills',
      'analytics.view',
      'analytics.team',
    ],
    member: [
      'team.view',
      'team.manage_tickets',
    ],
    client: [
      'portal.view',
      'portal.create_ticket',
      'portal.reply_ticket',
      'portal.upload_attachments',
      'portal.view_results',
    ],
  };

  return permissionMap[role] || permissionMap.client;
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(context: UserContext, permission: string): boolean {
  return context.permissions.includes(permission);
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(context: UserContext, permissions: string[]): boolean {
  return permissions.some(p => context.permissions.includes(p));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(context: UserContext, permissions: string[]): boolean {
  return permissions.every(p => context.permissions.includes(p));
}